import { getFieldRules } from 'src/components/ui/inputs/FieldSet/FieldSet.utils'
import { $te, translate } from 'src/plugins/i18n'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import { getEquivalentType, getFields } from 'src/services/mappings/getMappings'
import type {
  ContextualFieldInterface,
  FieldInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import {
  FIELD_RULES,
  type FieldStatusesByOriginKey,
} from 'src/tpbp-core/models/fields/fieldRules.const'
import type { EnumInterface, Scalar } from 'src/tpbp-core/models/global.type'
import type { PersonModel } from 'src/tpbp-json-api-handler/models/models'
import {
  getConfigurationFieldKey,
  getSchema,
} from 'src/tpbp-json-api-handler/utils/ApiStore'
import { getFieldEnums } from 'src/utils/modelLabels.utils'
import { evalExpr, evalFieldStatusRules, getFirstValidRule } from './fieldRules'

export function evalFieldStatus(
  field: FieldInterface,
  record: ProxyRecord | undefined,
  fieldStatusesByOriginKey: FieldStatusesByOriginKey,
) {
  if (field.rule) return field.rule
  if (field.dynamicRules)
    return evalFieldStatusRules(field, record, fieldStatusesByOriginKey)
  return
}

export function getFieldContextualEnumKeys(
  field: FieldInterface,
  record: ProxyRecord | undefined,
  context: Record<string, unknown>,
) {
  if (field.enumRules) {
    const rule = getFirstValidRule(field.enumRules, record, field, context)
    if (rule?.getEnumKeys) {
      const list = evalExpr(rule.getEnumKeys, record, field, context)
      return list as typeof field.enumKeys
    }
  }
  if (field.getEnums && !field.enumKeys) {
    return []
  }
  return field.enumKeys
}

function getJsonSchemaFieldLabel(
  field: FieldInterface,
  record: Record<string, unknown> | undefined,
) {
  if (!record?.productKind) return ''
  const schema = getSchema(record as unknown as ProxyRecord)
  const property = schema?.properties[field.originKey]
  return property?.label || ''
}

export function getFieldLabel(
  field: FieldInterface,
  record: ProxyRecord | undefined,
): string {
  const dynamicLabel: string = field.dynamicLabel
    ? evalExpr(field.dynamicLabel, record, field, {})
    : ''
  if (dynamicLabel) return dynamicLabel
  const configurationFieldKey = getConfigurationFieldKey(record?.$type ?? '')
  const jsonSchemaFieldLabel = configurationFieldKey
    ? getJsonSchemaFieldLabel(
        field,
        record as Record<string, unknown> | undefined,
      )
    : ''
  return (
    jsonSchemaFieldLabel || getDefaultFieldLabel(record?.$type ?? '', field.key)
  )
}

export function getDefaultFieldLabel(
  objectType: string,
  fieldKey: string,
): string {
  return $te(`models.${objectType}.fields.${fieldKey}`)
    ? translate(`models.${objectType}.fields.${fieldKey}`)
    : ''
}

export function getContextualFields(
  record: ProxyRecord | undefined,
  currentUser: PersonModel | null = null,
  additionalContext = {},
) {
  const objectType = getEquivalentType(record?.$type ?? '')
  const fields = getFields(objectType) ?? []
  const fieldStatusesByOriginKey: FieldStatusesByOriginKey = {}

  return fields.map((field: FieldInterface) =>
    getContextualField({
      record,
      currentUser,
      additionalContext,
      field,
      objectType,
      fieldStatusesByOriginKey,
    }),
  )
}

type GetContextualFieldParameters = {
  record: ProxyRecord | undefined
  currentUser: PersonModel | null
  additionalContext: Record<string, unknown>
  field: FieldInterface
  objectType: string
  fieldStatusesByOriginKey: FieldStatusesByOriginKey
}

function getContextualField({
  record,
  currentUser,
  additionalContext,
  field,
  objectType,
  fieldStatusesByOriginKey,
}: GetContextualFieldParameters) {
  const {
    key,
    originKey,
    getEnums,
    default: fieldDefault,
    getHelpMessageKey,
  } = field
  const label = getFieldLabel(field, record)

  const placeholderKey = `models.${objectType}.placeholders.${key}`
  const placeholder = $te(placeholderKey) ? translate(placeholderKey) : ''

  const helpMessageKey = getHelpMessageKey
    ? getHelpMessageKey(record as unknown as Record<string, unknown>)
    : ''
  const helpMessage = helpMessageKey === '' ? '' : translate(helpMessageKey)

  const status = evalFieldStatus(field, record, fieldStatusesByOriginKey)
  const rules = status ? getFieldRules(field, status, record) : []
  const fieldEnums = getFieldContextualEnumKeys(field, record, {
    currentUser,
    ...additionalContext,
  })

  let enums: ContextualFieldInterface['enums']
  if (fieldEnums === undefined) enums = undefined
  else if (typeof fieldEnums[0] === 'object')
    enums = fieldEnums as EnumInterface[]
  else
    enums = getFieldEnums(
      record as ProxyRecord,
      key,
      fieldEnums as Scalar[],
      getEnums,
    )

  fieldStatusesByOriginKey[originKey] = status

  return {
    ...field,
    label,
    placeholder,
    enums,
    rules,
    status,
    helpMessage,
    readOnly: status === FIELD_RULES.isReadOnly,
    default: fieldDefault,
  } as ContextualFieldInterface
}
